import type { PatientDto, VisitDto } from "@repo/data-store";
import { DataTableFilter, useDataTableFilters } from "@repo/ui/components/data-table-filter";
import { createColumnConfigHelper } from "@repo/ui/components/data-table-filter/core/filters";
import type { FilterModel } from "@repo/ui/components/data-table-filter/core/types";
import { optionFilterFn } from "@repo/ui/components/data-table-filter/lib/filter-fns";
import { Button } from "@repo/ui/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@repo/ui/components/ui/dropdown-menu";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { Status, StatusLabel } from "@repo/ui/components/ui/status";
import { Switch } from "@repo/ui/components/ui/switch";
import { useNavigate } from "@tanstack/react-router";
import { differenceInHours, differenceInMinutes, format, isToday } from "date-fns";
import { Activity, CalendarDays, Settings2, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useNow } from "@/shared/lib/clock-store";
import { getMRN, getPatientName } from "@/shared/lib/patient-utils";

const dtf = createColumnConfigHelper<PatientDto>();

const filterColumnsConfig = [
  dtf
    .option()
    .id("gender")
    .accessor((row) => row.gender ?? "unknown")
    .displayName("Gender")
    .icon(Users)
    .transformOptionFn((value) => ({
      label: value.charAt(0).toUpperCase() + value.slice(1),
      value,
    }))
    .build(),
  dtf
    .option()
    .id("status")
    .accessor((row) => (row.active ? "Active" : "Inactive"))
    .displayName("Status")
    .icon(Activity)
    .transformOptionFn((value) => ({ label: value, value }))
    .build(),
] as const;

function formatCountdown(now: Date, target: Date): string {
  const totalMins = differenceInMinutes(target, now);
  if (totalMins <= 0) return "now";
  const hours = differenceInHours(target, now);
  const mins = totalMins % 60;
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}

function AppointmentBadge({ visit }: { visit: VisitDto | undefined }) {
  const now = useNow();

  if (!visit?.start) {
    return (
      <Status variant="default">
        <CalendarDays className="size-3" />
        <StatusLabel>No appointment</StatusLabel>
      </Status>
    );
  }

  if (visit.status === "inProgress") {
    return (
      <Status variant="warning">
        <CalendarDays className="size-3" />
        <StatusLabel>In Progress</StatusLabel>
      </Status>
    );
  }

  if (visit.status === "checkedIn") {
    return (
      <Status variant="warning">
        <CalendarDays className="size-3" />
        <StatusLabel>Waiting</StatusLabel>
      </Status>
    );
  }

  const startDate = new Date(visit.start);
  const endDate = visit.end ? new Date(visit.end) : null;
  if (visit.status === "booked" && startDate <= now && endDate && endDate >= now) {
    return (
      <Status variant="error">
        <CalendarDays className="size-3" />
        <StatusLabel>Not Checked In</StatusLabel>
      </Status>
    );
  }

  const isSameDay = isToday(startDate);
  const label = isSameDay ? formatCountdown(now, startDate) : format(startDate, "MMM d h:mm a");

  return (
    <Status variant={isSameDay ? "warning" : "info"}>
      <CalendarDays className="size-3" />
      <StatusLabel>{label}</StatusLabel>
    </Status>
  );
}

interface PatientsListProps {
  patients: PatientDto[];
  nextVisits: Map<string, VisitDto>;
  loading?: boolean;
}

export function PatientsList({ patients, nextVisits, loading = false }: PatientsListProps) {
  const navigate = useNavigate();

  const {
    columns: filterColumns,
    filters,
    actions,
    strategy,
  } = useDataTableFilters({
    strategy: "client",
    data: patients,
    columnsConfig: filterColumnsConfig,
  });

  const [activeOnly, setActiveOnly] = useState(false);

  const filteredData = useMemo(() => {
    const base = activeOnly ? patients.filter((p) => p.active) : patients;
    if (filters.length === 0) return base;

    return base.filter((row) => {
      return filters.every((filter) => {
        const columnConfig = filterColumnsConfig.find((c) => c.id === filter.columnId);
        if (!columnConfig) return true;

        const value = columnConfig.accessor(row);
        if (filter.type === "option") {
          return optionFilterFn(value as string, filter as FilterModel<"option">);
        }
        return true;
      });
    });
  }, [patients, filters, activeOnly]);

  if (loading) {
    return (
      <div className="flex h-full w-full flex-col">
        <div className="border-b px-4 py-3">
          <Skeleton className="h-9 w-64" />
        </div>
        <div className="flex-1 p-4 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-2 py-3 flex items-center justify-between">
        <DataTableFilter filters={filters} columns={filterColumns} actions={actions} strategy={strategy} locale="en" />
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="secondary" size="icon" />}>
            <Settings2 className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Button
              variant="ghost"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm h-auto justify-start font-normal"
              onClick={() => setActiveOnly((v) => !v)}
            >
              <span>Show active only</span>
              <Switch checked={activeOnly} className="ml-auto pointer-events-none" tabIndex={-1} />
            </Button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {filteredData.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No patients found.</div>
        ) : (
          filteredData.map((patient) => {
            const mrn = getMRN(patient);
            const nextVisit = nextVisits.get(patient.patientId);

            return (
              <Button
                variant="ghost"
                key={patient.patientId}
                className="grid w-full grid-cols-[1fr_160px] items-center gap-3 border-b px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer text-left h-auto rounded-none"
                onClick={() =>
                  navigate({
                    to: "/patients/$patientId",
                    params: { patientId: patient.patientId },
                  })
                }
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-sm">{getPatientName(patient)}</span>
                  {mrn !== "-" && <span className="text-xs text-muted-foreground">MRN: {mrn}</span>}
                </div>
                <div className="flex justify-start">
                  <AppointmentBadge visit={nextVisit} />
                </div>
              </Button>
            );
          })
        )}
      </div>
    </div>
  );
}
